import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flag, ArrowLeft, Plus, Search, FileDown, BarChart3, AlertTriangle, Trash2, X, Zap, UserCheck, Calendar, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import AccionesGestion from './AccionesGestion';
import DetalleGestion from './DetalleGestion';
import EditarGestion from './EditarGestion';
import EstadisticasModulo from './EstadisticasModulo';
import RichTextEditor from './RichTextEditor';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para verificar si una fecha está vencida para CNC
// Si ya fue derivada a Probidad, NO se considera vencido para CNC
// Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
const esVencidoCNC = (fecha, estado, asesorProbidadId, asesorProbidadNombre) => {
  if (!fecha || estado === 'Completada / Archivada') return false;
  // Si ya fue derivada a Probidad, fue atendida en tiempo para CNC
  if (asesorProbidadId || asesorProbidadNombre) return false;
  const fechaVenc = parseLocalDate(fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return fechaVenc && hoy > fechaVenc;
};

// Helper para verificar si fue atendida en tiempo (derivada a Probidad aunque pasó fecha)
const esAtendidoEnTiempo = (fecha, estado, asesorProbidadId, asesorProbidadNombre) => {
  if (!fecha || estado === 'Completada / Archivada') return false;
  const yaDerivada = asesorProbidadId || asesorProbidadNombre;
  const fechaVenc = parseLocalDate(fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return yaDerivada && fechaVenc && hoy > fechaVenc;
};

// Helper para verificar si está vencido para Probidad
// Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
const esVencidoProbidad = (fecha, estado) => {
  if (!fecha || estado === 'Completada / Archivada') return false;
  const fechaVenc = parseLocalDate(fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return fechaVenc && hoy > fechaVenc;
};

const ModuloBanderasRojas = ({ onBack }) => {
  const { user, canCreateCases, canEditCases, isAnalyst, canViewGestion } = useAuth();
  const [view, setView] = useState('list');
  const [gestiones, setGestiones] = useState([]);
  const [indicadores, setIndicadores] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  const [asesorSugerido, setAsesorSugerido] = useState(null);
  const [usarAsignacionAuto, setUsarAsignacionAuto] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedGestion, setSelectedGestion] = useState(null);
  // Catálogos dinámicos para Adquisiciones Públicas
  const [tiposConcurso, setTiposConcurso] = useState([]);
  const [estadosConcurso, setEstadosConcurso] = useState([]);
  const [filtros, setFiltros] = useState({
    q: '',
    estado: '',
    color_bandera: '',
    institucion: '',
    asesor_cnc: '',
    fecha_asignacion_desde: '',
    fecha_asignacion_hasta: '',
    fecha_vencimiento_cnc_desde: '',
    fecha_vencimiento_cnc_hasta: '',
    fecha_vencimiento_probidad_desde: '',
    fecha_vencimiento_probidad_hasta: ''
  });

  // Estado para el formulario de creación
  const [formData, setFormData] = useState({
    institucion: '',
    nombre_asesor_cnc: '',
    nombre_asesor_instancia: '',
    fecha_asignacion: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    descripcion: '',
    links_referenciales: [],
    campos_especificos: {
      color_bandera: '',
      indicador_id: '',
      indicador_codigo: '',
      fecha_inicio_analisis: '',
      fecha_fin_analisis: '',
      unidad_compradora: '',
      cantidad_nog: 0,
      cantidad_npg: 0,
      enlaces_guatecompras: [],
      detalle_nogs: [],
      detalle_npgs: [],
      proveedores: []
    }
  });
  const [currentLink, setCurrentLink] = useState('');
  const [currentGuatecomprasLink, setCurrentGuatecomprasLink] = useState('');
  const [showNogModal, setShowNogModal] = useState(false);
  const [showNpgModal, setShowNpgModal] = useState(false);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepErrors, setStepErrors] = useState({});
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  const [currentNog, setCurrentNog] = useState({
    nog: '',
    descripcion: '',
    tipo_concurso: '',
    status_concurso: '',
    fecha_adjudicacion: '',
    monto: ''
  });
  const [currentNpg, setCurrentNpg] = useState({
    npg: '',
    descripcion: '',
    tipo_concurso: '',
    status_concurso: '',
    fecha_adjudicacion: '',
    monto: ''
  });
  const [currentProveedor, setCurrentProveedor] = useState({
    nombre: '',
    nit: '',
    oferta: ''
  });

  // Obtener asesor con menor carga de trabajo para la institución (auto-asignación Fase 1)
  const fetchAsesorDisponible = useCallback(async (institucion = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (institucion) {
        params.institucion = institucion;
      }
      console.log('Buscando asesor para institución:', institucion || 'ninguna');
      const response = await axios.get(`${API}/asignacion/asesor-cnc-disponible`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data.asesor) {
        console.log('Asesor encontrado:', response.data.asesor.nombre_completo, 'Grupo:', response.data.asesor.grupo_nombre);
        setAsesorSugerido(response.data);
        // Auto-asignar si está activado
        if (usarAsignacionAuto) {
          setFormData(prev => ({
            ...prev,
            nombre_asesor_cnc: response.data.asesor.nombre_completo
          }));
        }
      } else {
        setAsesorSugerido(null);
      }
    } catch (error) {
      console.error('Error al obtener asesor disponible:', error);
    }
  }, [usarAsignacionAuto]);

  useEffect(() => {
    fetchGestiones();
    fetchIndicadores();
    fetchInstituciones();
    fetchAsesores();
    fetchAsesorDisponible();
    fetchCatalogosAdquisiciones();
  }, []);

  // Actualizar asesor sugerido cuando cambia la institución en el formulario
  useEffect(() => {
    if (formData.institucion) {
      fetchAsesorDisponible(formData.institucion);
    }
  }, [formData.institucion, fetchAsesorDisponible]);

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      // Buscar con ambos nombres para compatibilidad
      const [response1, response2] = await Promise.all([
        axios.get(`${API}/gestiones?tipo_gestion=Adquisiciones públicas`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Banderas Rojas`).catch(() => ({ data: [] }))
      ]);
      let allGestiones = [...(response1.data || []), ...(response2.data || [])];
      const uniqueGestiones = allGestiones.filter((g, index, self) => 
        index === self.findIndex(t => t.id === g.id)
      );
      
      // Filtrar gestiones para Analista (solo ve las asignadas a él)
      if (isAnalyst() && user) {
        allGestiones = uniqueGestiones.filter(g => canViewGestion(g));
      } else {
        allGestiones = uniqueGestiones;
      }
      
      setGestiones(allGestiones);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar las gestiones');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndicadores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/indicadores-banderas-rojas?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndicadores(response.data || []);
    } catch (error) {
      console.error('Error al cargar indicadores:', error);
    }
  };

  const fetchInstituciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/instituciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // El nuevo endpoint devuelve objetos con {id, nombre, tipo}
      const instituciones = (response.data || []).map(inst => ({
        id: inst.id || inst.nombre,
        value: inst.nombre,
        is_active: true
      }));
      setInstituciones(instituciones);
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
    }
  };

  const fetchAsesores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/asesores-cnc?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesores(response.data || []);
      setAsesoresCNC(response.data || []); // También para filtros
    } catch (error) {
      console.error('Error al cargar asesores:', error);
    }
  };

  // Cargar catálogos de Tipo Concurso y Estado Concurso para Adquisiciones Públicas
  const fetchCatalogosAdquisiciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tiposRes, estadosRes] = await Promise.all([
        axios.get(`${API}/catalogs/tipo_concurso`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/catalogs/estado_concurso`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTiposConcurso((tiposRes.data || []).filter(t => t.is_active !== false));
      setEstadosConcurso((estadosRes.data || []).filter(e => e.is_active !== false));
    } catch (error) {
      console.error('Error al cargar catálogos de adquisiciones:', error);
    }
  };

  const handleExportarExcel = () => {
    if (gestiones.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = gestiones.map(g => ({
      'Nomenclatura': g.nomenclatura,
      'Fecha Asignación': formatLocalDate(g.fecha_asignacion),
      'Color Bandera': g.campos_especificos?.color_bandera || 'N/A',
      'Indicador': g.campos_especificos?.indicador_codigo || 'N/A',
      'Institución': g.institucion,
      'Asesor CNC': g.nombre_asesor_cnc,
      'Fecha Vencimiento CNC': formatLocalDate(g.fecha_vencimiento),
      'Asignado a Probidad': g.asesor_probidad_nombre ? 'Sí' : 'No',
      'Asesor Probidad': g.asesor_probidad_nombre || 'N/A',
      'Fecha Vencimiento Probidad': formatLocalDate(g.fecha_vencimiento_probidad),
      'Estado': g.estado
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Adquisiciones Públicas');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Adquisiciones_Publicas_${fecha}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const handleBuscar = () => {
    let result = [...gestiones];
    
    // Búsqueda por texto
    if (filtros.q) {
      const q = filtros.q.toLowerCase();
      result = result.filter(g => 
        g.nomenclatura?.toLowerCase().includes(q) ||
        g.descripcion?.toLowerCase().includes(q) ||
        g.institucion?.toLowerCase().includes(q) ||
        g.nombre_asesor_cnc?.toLowerCase().includes(q) ||
        g.campos_especificos?.nombre_proveedor?.toLowerCase().includes(q) ||
        g.campos_especificos?.entidad_compradora?.toLowerCase().includes(q)
      );
    }
    
    // Filtro por estado
    if (filtros.estado) {
      result = result.filter(g => g.estado === filtros.estado);
    }
    
    // Filtro por color de bandera
    if (filtros.color_bandera) {
      result = result.filter(g => g.campos_especificos?.color_bandera === filtros.color_bandera);
    }
    
    // Filtro por institución
    if (filtros.institucion) {
      result = result.filter(g => g.institucion === filtros.institucion);
    }
    
    // Filtro por asesor CNC
    if (filtros.asesor_cnc) {
      result = result.filter(g => g.nombre_asesor_cnc === filtros.asesor_cnc);
    }
    
    // Filtro por fecha de asignación CNC
    if (filtros.fecha_asignacion_desde) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) >= new Date(filtros.fecha_asignacion_desde));
    }
    if (filtros.fecha_asignacion_hasta) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) <= new Date(filtros.fecha_asignacion_hasta));
    }
    
    // Filtro por fecha de vencimiento CNC
    if (filtros.fecha_vencimiento_cnc_desde) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) >= new Date(filtros.fecha_vencimiento_cnc_desde));
    }
    if (filtros.fecha_vencimiento_cnc_hasta) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) <= new Date(filtros.fecha_vencimiento_cnc_hasta));
    }
    
    // Filtro por fecha de vencimiento Instancia Probidad
    if (filtros.fecha_vencimiento_probidad_desde) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) >= new Date(filtros.fecha_vencimiento_probidad_desde));
    }
    if (filtros.fecha_vencimiento_probidad_hasta) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) <= new Date(filtros.fecha_vencimiento_probidad_hasta));
    }
    
    setGestiones(result);
    toast.success(`${result.length} gestiones encontradas`);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      q: '',
      estado: '',
      color_bandera: '',
      institucion: '',
      asesor_cnc: '',
      fecha_asignacion_desde: '',
      fecha_asignacion_hasta: '',
      fecha_vencimiento_cnc_desde: '',
      fecha_vencimiento_cnc_hasta: '',
      fecha_vencimiento_probidad_desde: '',
      fecha_vencimiento_probidad_hasta: ''
    });
    fetchGestiones();
  };

  const handleViewDetail = (gestion) => {
    setSelectedGestion(gestion);
    setView('detail');
  };

  const handleEditGestion = (gestion) => {
    setSelectedGestion(gestion);
    setView('edit');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCampoEspecifico = (field, value) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        [field]: value
      }
    }));
  };

  const handleIndicadorChange = (indicadorId) => {
    const indicador = indicadores.find(i => i.id === indicadorId);
    if (indicador) {
      setFormData(prev => ({
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          indicador_id: indicadorId,
          indicador_codigo: indicador.codigo || `IND-${indicador.id.slice(0,4).toUpperCase()}`
        }
      }));
    }
  };

  const handleAddLink = () => {
    if (currentLink.trim()) {
      setFormData(prev => ({
        ...prev,
        links_referenciales: [...prev.links_referenciales, currentLink.trim()]
      }));
      setCurrentLink('');
    }
  };

  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links_referenciales: prev.links_referenciales.filter((_, i) => i !== index)
    }));
  };

  const handleAddNog = () => {
    if (currentNog.nog) {
      setFormData(prev => {
        const nuevosNogs = [...(prev.campos_especificos.detalle_nogs || []), {...currentNog}];
        // Solo actualizar cantidad_nog si es menor al nuevo count
        const nuevaCantidad = Math.max(
          prev.campos_especificos.cantidad_nog || 0,
          nuevosNogs.length
        );
        return {
          ...prev,
          campos_especificos: {
            ...prev.campos_especificos,
            detalle_nogs: nuevosNogs,
            cantidad_nog: nuevaCantidad
          }
        };
      });
      setCurrentNog({
        nog: '',
        descripcion: '',
        tipo_concurso: '',
        status_concurso: '',
        fecha_adjudicacion: '',
        monto: ''
      });
      setShowNogModal(false);
      toast.success('NOG agregado');
    } else {
      toast.error('El número de NOG es requerido');
    }
  };

  const handleAddNpg = () => {
    if (currentNpg.npg) {
      setFormData(prev => {
        const nuevosNpgs = [...(prev.campos_especificos.detalle_npgs || []), {...currentNpg}];
        // Solo actualizar cantidad_npg si es menor al nuevo count
        const nuevaCantidad = Math.max(
          prev.campos_especificos.cantidad_npg || 0,
          nuevosNpgs.length
        );
        return {
          ...prev,
          campos_especificos: {
            ...prev.campos_especificos,
            detalle_npgs: nuevosNpgs,
            cantidad_npg: nuevaCantidad
          }
        };
      });
      setCurrentNpg({
        npg: '',
        descripcion: '',
        tipo_concurso: '',
        status_concurso: '',
        fecha_adjudicacion: '',
        monto: ''
      });
      setShowNpgModal(false);
      toast.success('NPG agregado');
    } else {
      toast.error('El número de NPG es requerido');
    }
  };

  const handleAddProveedor = () => {
    if (currentProveedor.nombre && currentProveedor.nit) {
      setFormData(prev => ({
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          proveedores: [...(prev.campos_especificos.proveedores || []), {...currentProveedor}]
        }
      }));
      setCurrentProveedor({
        nombre: '',
        nit: '',
        oferta: ''
      });
      setShowProveedorModal(false);
      toast.success('Proveedor agregado');
    } else {
      toast.error('Nombre y NIT del proveedor son requeridos');
    }
  };

  const handleRemoveNog = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        detalle_nogs: prev.campos_especificos.detalle_nogs.filter((_, i) => i !== index),
        cantidad_nog: Math.max(0, (prev.campos_especificos.cantidad_nog || 0) - 1)
      }
    }));
  };

  const handleRemoveNpg = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        detalle_npgs: prev.campos_especificos.detalle_npgs.filter((_, i) => i !== index),
        cantidad_npg: Math.max(0, (prev.campos_especificos.cantidad_npg || 0) - 1)
      }
    }));
  };

  const handleRemoveProveedor = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        proveedores: prev.campos_especificos.proveedores.filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddGuatecomprasLink = () => {
    if (currentGuatecomprasLink.trim()) {
      setFormData(prev => ({
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          enlaces_guatecompras: [...(prev.campos_especificos.enlaces_guatecompras || []), currentGuatecomprasLink.trim()]
        }
      }));
      setCurrentGuatecomprasLink('');
    }
  };

  const handleRemoveGuatecomprasLink = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        enlaces_guatecompras: prev.campos_especificos.enlaces_guatecompras.filter((_, i) => i !== index)
      }
    }));
  };

  const WIZARD_STEPS = [
    { num: 1, title: 'Datos Generales', desc: 'Institución y asignación' },
    { num: 2, title: 'Bandera', desc: 'Color e indicador' },
    { num: 3, title: 'Compra Pública', desc: 'Unidad y enlaces' },
    { num: 4, title: 'NOGs / NPGs', desc: 'Detalle de operaciones' },
    { num: 5, title: 'Proveedores y Descripción', desc: 'Datos finales' }
  ];

  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 1:
        if (!formData.institucion) errors.institucion = 'Institución es requerida';
        if (!formData.nombre_asesor_cnc) errors.nombre_asesor_cnc = 'Asesor es requerido';
        if (!formData.fecha_asignacion) errors.fecha_asignacion = 'Fecha de asignación es requerida';
        break;
      case 2:
        if (!formData.campos_especificos.color_bandera) errors.color_bandera = 'Color de bandera es requerido';
        if (!formData.campos_especificos.indicador_id) errors.indicador_id = 'Indicador es requerido';
        break;
      case 3:
        if (!formData.campos_especificos.unidad_compradora) errors.unidad_compradora = 'Unidad compradora es requerida';
        break;
      default:
        break;
    }
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error('Complete los campos requeridos antes de continuar');
    }
  };

  const handlePrevStep = () => {
    setStepErrors({});
    setShowConfirmacion(false);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Protección: solo permitir submit en el paso 5 con confirmación activa
    if (currentStep !== 5 || !showConfirmacion) {
      return;
    }
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tipo_gestion: 'Adquisiciones públicas',
        estado: 'Registrado' // Estado inicial por defecto
      };

      await axios.post(`${API}/gestiones`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Gestión de Adquisiciones Públicas creada exitosamente');
      fetchGestiones();
      setView('list');
      setCurrentStep(1);
      setStepErrors({});
      setShowConfirmacion(false);
      // Reset form
      setFormData({
        institucion: '',
        nombre_asesor_cnc: '',
        nombre_asesor_instancia: '',
        fecha_asignacion: new Date().toISOString().split('T')[0],
        fecha_vencimiento: '',
        descripcion: '',
        links_referenciales: [],
        campos_especificos: {
          color_bandera: '',
          indicador_id: '',
          indicador_codigo: '',
          fecha_inicio_analisis: '',
          fecha_fin_analisis: '',
          unidad_compradora: '',
          cantidad_nog: 0,
          cantidad_npg: 0,
          enlaces_guatecompras: [],
          detalle_nogs: [],
          detalle_npgs: [],
          proveedores: []
        }
      });
    } catch (error) {
      console.error('Error al crear gestión:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la gestión');
    } finally {
      setLoading(false);
    }
  };

  const getColorBanderaBadge = (color) => {
    const colors = {
      'Roja': { bg: '#FFEBEE', text: '#C62828', border: '#EF5350' },
      'Amarilla': { bg: '#FFF8E1', text: '#F57F17', border: '#FFB300' },
      'Verde': { bg: '#E8F5E9', text: '#2E7D32', border: '#66BB6A' }
    };
    return colors[color] || { bg: '#ECEFF1', text: '#546E7A', border: '#90A4AE' };
  };

  // Vista de detalle
  if (view === 'detail' && selectedGestion) {
    return (
      <div className="modulo-container">
        <DetalleGestion 
          gestionId={selectedGestion.id} 
          onBack={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
          }} 
        />
      </div>
    );
  }

  // Vista de edición
  if (view === 'edit' && selectedGestion) {
    return (
      <div className="modulo-container">
        <EditarGestion 
          gestion={selectedGestion}
          onSuccess={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
            toast.success('Gestión actualizada correctamente');
          }}
          onCancel={() => {
            setSelectedGestion(null);
            setView('list');
          }}
        />
      </div>
    );
  }

  return (
    <div className="modulo-container">
      <div className="modulo-header-bar">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Volver a Módulos
        </button>
        
        <div className="modulo-title-section">
          <div className="modulo-icon-header" style={{ backgroundColor: '#F44336' }}>
            <Flag size={24} />
          </div>
          <div>
            <h1 className="modulo-main-title">Adquisiciones públicas</h1>
            <p className="modulo-nomenclatura">Nomenclatura: AP-###</p>
          </div>
        </div>
      </div>

      <div className="modulo-tabs">
        <button 
          className={`modulo-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Listado de Casos
        </button>
        {canCreateCases() && (
          <button 
            className={`modulo-tab ${view === 'create' ? 'active' : ''}`}
            onClick={() => setView('create')}
          >
            <Plus size={16} />
            Nueva Gestión
          </button>
        )}
        <button 
          className={`modulo-tab ${view === 'stats' ? 'active' : ''}`}
          onClick={() => setView('stats')}
        >
          <BarChart3 size={16} />
          Estadísticas
        </button>
      </div>

      <div className="modulo-content">
        {view === 'list' && (
          <div>
            {/* Filtros Ampliados */}
            <div className="panel filters-compact" style={{ marginBottom: '1.5rem' }}>
              {/* Fila 1: Búsqueda principal y filtros de categoría */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Búsqueda General</label>
                  <input
                    type="text"
                    placeholder="Nomenclatura, proveedor, entidad..."
                    value={filtros.q}
                    onChange={(e) => setFiltros({...filtros, q: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Institución</label>
                  <select value={filtros.institucion} onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}>
                    <option value="">Todas</option>
                    {instituciones.map(inst => (
                      <option key={inst.id} value={inst.value}>{inst.value}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Asesor CNC</label>
                  <select value={filtros.asesor_cnc} onChange={(e) => setFiltros({...filtros, asesor_cnc: e.target.value})}>
                    <option value="">Todos</option>
                    {asesoresCNC.map(asesor => (
                      <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Estado</label>
                  <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})}>
                    <option value="">Todos</option>
                    <option value="Registrado">Registrado</option>
                    <option value="Asignada">Asignada</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Enviado / Compartido">Enviado / Compartido</option>
                    <option value="En espera">En espera</option>
                    <option value="En revisión">En revisión</option>
                    <option value="Derivado a probidad">Derivado a probidad</option>
                    <option value="Completada / Archivada">Completada / Archivada</option>
                  </select>
                </div>
              </div>
              
              {/* Fila 2: Filtros específicos del módulo y fechas */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Color Bandera</label>
                  <select value={filtros.color_bandera} onChange={(e) => setFiltros({...filtros, color_bandera: e.target.value})}>
                    <option value="">Todos</option>
                    <option value="Roja">Roja</option>
                    <option value="Amarilla">Amarilla</option>
                    <option value="Verde">Verde</option>
                  </select>
                </div>
                <div className="filter-item">
                  <label>Asig. CNC Desde</label>
                  <input type="date" value={filtros.fecha_asignacion_desde} onChange={(e) => setFiltros({...filtros, fecha_asignacion_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Asig. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_asignacion_hasta} onChange={(e) => setFiltros({...filtros, fecha_asignacion_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. IP Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_desde: e.target.value})} />
                </div>
              </div>
              
              {/* Fila 3: Más filtros de fecha y botones */}
              <div className="filters-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="filter-item">
                  <label>Venc. IP Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_hasta: e.target.value})} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                  <button className="btn btn-primary" onClick={handleBuscar}>
                    <Search size={16} />
                    Buscar
                  </button>
                  <button className="btn btn-secondary" onClick={handleLimpiarFiltros}>
                    Limpiar
                  </button>
                </div>
              </div>
            </div>

            {/* Tabla de gestiones */}
            <div className="panel">
              <div className="panel-header">
                <h2>Gestiones de Adquisiciones Públicas ({gestiones.length})</h2>
                <button className="btn btn-secondary" onClick={handleExportarExcel}>
                  <FileDown size={16} />
                  Exportar Excel
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="loader"></div>
                </div>
              ) : gestiones.length === 0 ? (
                <div className="empty-state">
                  <Flag size={48} style={{ opacity: 0.3 }} />
                  <p>No hay gestiones de banderas rojas registradas</p>
                  {canCreateCases() && (
                    <button className="btn btn-primary" onClick={() => setView('create')}>
                      <Plus size={16} />
                      Crear Primera Gestión
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Nomenclatura</th>
                        <th>Fecha</th>
                        <th>Color</th>
                        <th>Indicador</th>
                        <th>Institución</th>
                        <th>Asesor CNC</th>
                        <th>Venc. CNC</th>
                        <th>Probidad</th>
                        <th>Asesor Probidad</th>
                        <th>Venc. Probidad</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestiones.map((gestion) => {
                        const colorStyle = getColorBanderaBadge(gestion.campos_especificos?.color_bandera);
                        return (
                          <tr key={gestion.id}>
                            <td><strong>{gestion.nomenclatura}</strong></td>
                            <td>{formatLocalDate(gestion.fecha_asignacion)}</td>
                            <td>
                              <span 
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  backgroundColor: colorStyle.bg,
                                  color: colorStyle.text,
                                  border: `1px solid ${colorStyle.border}`
                                }}
                              >
                                <Flag size={12} />
                                {gestion.campos_especificos?.color_bandera || 'N/A'}
                              </span>
                            </td>
                            <td><code style={{ fontSize: '0.75rem' }}>{gestion.campos_especificos?.indicador_codigo || 'N/A'}</code></td>
                            <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {gestion.institucion || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                            </td>
                            <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {gestion.nombre_asesor_cnc || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                            </td>
                            <td>
                              {gestion.fecha_vencimiento ? (
                                (() => {
                                  const vencidoCNC = esVencidoCNC(gestion.fecha_vencimiento, gestion.estado, gestion.asesor_probidad_id, gestion.asesor_probidad_nombre);
                                  const atendidoTiempo = esAtendidoEnTiempo(gestion.fecha_vencimiento, gestion.estado, gestion.asesor_probidad_id, gestion.asesor_probidad_nombre);
                                  return (
                                    <span className={vencidoCNC ? 'text-danger font-bold' : atendidoTiempo ? 'text-success font-bold' : ''}>
                                      {vencidoCNC && <AlertTriangle size={12} style={{ marginRight: '2px', verticalAlign: 'middle' }} />}
                                      {atendidoTiempo && <span style={{ marginRight: '2px', color: '#2E7D32' }}>✓</span>}
                                      {formatLocalDate(gestion.fecha_vencimiento)}
                                    </span>
                                  );
                                })()
                              ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                              )}
                            </td>
                            <td>
                              {gestion.asesor_probidad_nombre ? (
                                <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                                  <UserCheck size={12} style={{ marginRight: '2px' }} />
                                  Sí
                                </span>
                              ) : (
                                <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>No</span>
                              )}
                            </td>
                            <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {gestion.asesor_probidad_nombre || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                            </td>
                            <td>
                              {gestion.fecha_vencimiento_probidad ? (
                                <span className={esVencidoProbidad(gestion.fecha_vencimiento_probidad, gestion.estado) ? 'text-danger font-bold' : ''}>
                                  {esVencidoProbidad(gestion.fecha_vencimiento_probidad, gestion.estado) && <AlertTriangle size={12} style={{ marginRight: '2px', verticalAlign: 'middle' }} />}
                                  {formatLocalDate(gestion.fecha_vencimiento_probidad)}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${
                                gestion.estado === 'Completada / Archivada' ? 'badge-success' : 
                                gestion.estado === 'En proceso de análisis' ? 'badge-info' : 
                                'badge-warning'
                              }`}>
                                {gestion.estado}
                              </span>
                            </td>
                            <td>
                              <AccionesGestion 
                                gestion={gestion}
                                onView={handleViewDetail}
                                onEdit={handleEditGestion}
                                compact={true}
                                canEdit={canEditCases()}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'create' && (
          <div className="wizard-container" data-testid="wizard-adquisiciones">
            {/* Progress Bar */}
            <div className="wizard-progress" data-testid="wizard-progress">
              {WIZARD_STEPS.map((step) => (
                <div 
                  key={step.num} 
                  className={`wizard-step-indicator ${currentStep === step.num ? 'active' : ''} ${currentStep > step.num ? 'completed' : ''}`}
                  onClick={() => { if (currentStep > step.num) { setStepErrors({}); setCurrentStep(step.num); } }}
                  style={{ cursor: currentStep > step.num ? 'pointer' : 'default' }}
                >
                  <div className="step-circle">
                    {currentStep > step.num ? <Check size={14} /> : step.num}
                  </div>
                  <div className="step-label">
                    <span className="step-title">{step.title}</span>
                    <span className="step-desc">{step.desc}</span>
                  </div>
                  {step.num < 5 && <div className={`step-connector ${currentStep > step.num ? 'completed' : ''}`} />}
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Datos Generales */}
              {currentStep === 1 && (
                <div className="modulo-form-section" data-testid="wizard-step-1">
                  <h3 className="modulo-form-section-title">
                    <Calendar size={18} />
                    Paso 1: Datos Generales
                  </h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="required">Institución</label>
                      <select
                        value={formData.institucion}
                        onChange={(e) => handleChange('institucion', e.target.value)}
                        className={stepErrors.institucion ? 'input-error' : ''}
                        data-testid="wizard-institucion"
                      >
                        <option value="">Seleccione una institución</option>
                        {instituciones.map((inst) => (
                          <option key={inst.id} value={inst.value}>{inst.value}</option>
                        ))}
                      </select>
                      {stepErrors.institucion && <span className="field-error">{stepErrors.institucion}</span>}
                    </div>

                    <div className="form-field">
                      <label className="required">Asesor</label>
                      {asesorSugerido && (
                        <div style={{ 
                          background: asesorSugerido.asesor.grupo_id ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)' : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                          border: asesorSugerido.asesor.grupo_id ? '1px solid #4CAF50' : '1px solid #FF9800',
                          borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.8rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Zap size={16} color={asesorSugerido.asesor.grupo_id ? '#4CAF50' : '#FF9800'} />
                            <span>
                              <strong>Sugerido:</strong> {asesorSugerido.asesor.nombre_completo}
                              <span style={{ color: '#666', marginLeft: '0.5rem' }}>({asesorSugerido.casos_asignados} casos)</span>
                            </span>
                          </div>
                          {asesorSugerido.asesor.grupo_nombre && (
                            <div style={{ marginTop: '0.25rem', color: '#1976D2', fontSize: '0.75rem' }}>Grupo: {asesorSugerido.asesor.grupo_nombre}</div>
                          )}
                        </div>
                      )}
                      <select
                        value={formData.nombre_asesor_cnc}
                        onChange={(e) => { handleChange('nombre_asesor_cnc', e.target.value); setUsarAsignacionAuto(false); }}
                        className={stepErrors.nombre_asesor_cnc ? 'input-error' : ''}
                        data-testid="wizard-asesor"
                      >
                        <option value="">Seleccione un asesor</option>
                        {asesores.map((asesor) => (
                          <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
                        ))}
                      </select>
                      {stepErrors.nombre_asesor_cnc && <span className="field-error">{stepErrors.nombre_asesor_cnc}</span>}
                    </div>

                    <div className="form-field">
                      <label className="required">Fecha de Asignación</label>
                      <input
                        type="date"
                        value={formData.fecha_asignacion}
                        onChange={(e) => handleChange('fecha_asignacion', e.target.value)}
                        className={stepErrors.fecha_asignacion ? 'input-error' : ''}
                        data-testid="wizard-fecha-asignacion"
                      />
                      {stepErrors.fecha_asignacion && <span className="field-error">{stepErrors.fecha_asignacion}</span>}
                    </div>

                    <div className="form-field">
                      <label>Fecha de Vencimiento</label>
                      <input
                        type="date"
                        value={formData.fecha_vencimiento}
                        onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
                      />
                    </div>

                    <div className="form-field">
                      <label>
                        Instancia de Probidad
                        <span style={{ fontSize: '0.7rem', color: '#FF9800', marginLeft: '0.5rem' }}>(Se asigna en derivación)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.nombre_asesor_instancia}
                        disabled
                        placeholder="Se asignará después del análisis"
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Información de la Bandera */}
              {currentStep === 2 && (
                <div className="modulo-form-section" data-testid="wizard-step-2">
                  <h3 className="modulo-form-section-title">
                    <AlertTriangle size={18} />
                    Paso 2: Información de la Bandera Roja
                  </h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="required">Color de Bandera</label>
                      <select
                        value={formData.campos_especificos.color_bandera}
                        onChange={(e) => handleCampoEspecifico('color_bandera', e.target.value)}
                        className={stepErrors.color_bandera ? 'input-error' : ''}
                        data-testid="wizard-color-bandera"
                      >
                        <option value="">Seleccione color</option>
                        <option value="Roja">Roja (Alto Riesgo)</option>
                        <option value="Amarilla">Amarilla (Riesgo Medio)</option>
                        <option value="Verde">Verde (Bajo Riesgo)</option>
                      </select>
                      {stepErrors.color_bandera && <span className="field-error">{stepErrors.color_bandera}</span>}
                    </div>

                    <div className="form-field">
                      <label className="required">Indicador</label>
                      <select
                        value={formData.campos_especificos.indicador_id}
                        onChange={(e) => handleIndicadorChange(e.target.value)}
                        className={stepErrors.indicador_id ? 'input-error' : ''}
                        data-testid="wizard-indicador"
                      >
                        <option value="">Seleccione un indicador</option>
                        {indicadores.map((ind) => (
                          <option key={ind.id} value={ind.id}>
                            {ind.codigo ? `${ind.codigo} - ` : ''}{ind.nombre_categoria} - {ind.indicador?.substring(0, 50)}
                          </option>
                        ))}
                      </select>
                      {stepErrors.indicador_id && <span className="field-error">{stepErrors.indicador_id}</span>}
                    </div>

                    <div className="form-field">
                      <label>Fecha Inicio Análisis</label>
                      <input
                        type="date"
                        value={formData.campos_especificos.fecha_inicio_analisis}
                        onChange={(e) => handleCampoEspecifico('fecha_inicio_analisis', e.target.value)}
                      />
                    </div>

                    <div className="form-field">
                      <label>Fecha Fin Análisis</label>
                      <input
                        type="date"
                        value={formData.campos_especificos.fecha_fin_analisis}
                        onChange={(e) => handleCampoEspecifico('fecha_fin_analisis', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Datos de la Compra Pública */}
              {currentStep === 3 && (
                <div className="modulo-form-section" data-testid="wizard-step-3">
                  <h3 className="modulo-form-section-title">
                    Paso 3: Datos de la Compra Pública
                  </h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="required">Unidad Compradora</label>
                      <input
                        type="text"
                        value={formData.campos_especificos.unidad_compradora}
                        onChange={(e) => handleCampoEspecifico('unidad_compradora', e.target.value)}
                        placeholder="Nombre de la unidad compradora"
                        className={stepErrors.unidad_compradora ? 'input-error' : ''}
                        data-testid="wizard-unidad-compradora"
                      />
                      {stepErrors.unidad_compradora && <span className="field-error">{stepErrors.unidad_compradora}</span>}
                    </div>

                    <div className="form-field">
                      <label>Cantidad de NOG</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="number" min="0"
                          value={formData.campos_especificos.cantidad_nog ?? formData.campos_especificos.detalle_nogs?.length ?? 0}
                          onChange={(e) => handleCampoEspecifico('cantidad_nog', parseInt(e.target.value) || 0)}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                          (Detallados: {formData.campos_especificos.detalle_nogs?.length || 0})
                        </span>
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Cantidad de NPG</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="number" min="0"
                          value={formData.campos_especificos.cantidad_npg ?? formData.campos_especificos.detalle_npgs?.length ?? 0}
                          onChange={(e) => handleCampoEspecifico('cantidad_npg', parseInt(e.target.value) || 0)}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                          (Detallados: {formData.campos_especificos.detalle_npgs?.length || 0})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Enlaces Guatecompras */}
                  <div className="form-field" style={{ marginTop: '1rem' }}>
                    <label>Enlaces Guatecompras</label>
                    <div className="link-input-container">
                      <input
                        type="url"
                        value={currentGuatecomprasLink}
                        onChange={(e) => setCurrentGuatecomprasLink(e.target.value)}
                        placeholder="https://www.guatecompras.gt/..."
                      />
                      <button type="button" className="btn btn-secondary" onClick={handleAddGuatecomprasLink}>
                        <Plus size={16} /> Agregar
                      </button>
                    </div>
                    {formData.campos_especificos.enlaces_guatecompras?.length > 0 && (
                      <div className="links-list" style={{ marginTop: '0.5rem' }}>
                        {formData.campos_especificos.enlaces_guatecompras.map((link, index) => (
                          <div key={index} className="link-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
                            <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem' }}>{link}</a>
                            <button type="button" onClick={() => handleRemoveGuatecomprasLink(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}>
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Detalle NOGs y NPGs */}
              {currentStep === 4 && (
                <div data-testid="wizard-step-4">
                  {/* NOGs */}
                  <div className="modulo-form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Paso 4a: Detalle de NOGs</h3>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNogModal(true)}>
                        <Plus size={16} /> Agregar NOG
                      </button>
                    </div>
                    {formData.campos_especificos.detalle_nogs?.length > 0 ? (
                      <div className="table-wrapper">
                        <table className="detail-table">
                          <thead>
                            <tr><th>NOG</th><th>Descripción</th><th>Tipo Concurso</th><th>Estado</th><th>Fecha Adj.</th><th>Monto</th><th></th></tr>
                          </thead>
                          <tbody>
                            {formData.campos_especificos.detalle_nogs.map((nog, index) => (
                              <tr key={index}>
                                <td><strong>{nog.nog}</strong></td>
                                <td>{nog.descripcion || 'N/A'}</td>
                                <td>{nog.tipo_concurso}</td>
                                <td>{nog.status_concurso}</td>
                                <td>{nog.fecha_adjudicacion ? new Date(nog.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                                <td>{nog.monto ? `Q ${parseFloat(nog.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                                <td><button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveNog(index)}><Trash2 size={14} /></button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                        No hay NOGs agregados. Use el botón "Agregar NOG" para incluir detalles.
                      </p>
                    )}
                  </div>

                  {/* NPGs */}
                  <div className="modulo-form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Paso 4b: Detalle de NPGs (Concursos)</h3>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNpgModal(true)}>
                        <Plus size={16} /> Agregar NPG
                      </button>
                    </div>
                    {formData.campos_especificos.detalle_npgs?.length > 0 ? (
                      <div className="table-wrapper">
                        <table className="detail-table">
                          <thead>
                            <tr><th>NPG</th><th>Descripción</th><th>Tipo Concurso</th><th>Estado</th><th>Fecha Adj.</th><th>Monto</th><th></th></tr>
                          </thead>
                          <tbody>
                            {formData.campos_especificos.detalle_npgs.map((npg, index) => (
                              <tr key={index}>
                                <td><strong>{npg.npg}</strong></td>
                                <td>{npg.descripcion || 'N/A'}</td>
                                <td>{npg.tipo_concurso}</td>
                                <td>{npg.status_concurso}</td>
                                <td>{npg.fecha_adjudicacion ? new Date(npg.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                                <td>{npg.monto ? `Q ${parseFloat(npg.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                                <td><button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveNpg(index)}><Trash2 size={14} /></button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                        No hay NPGs agregados. Use el botón "Agregar NPG" para incluir detalles de concursos.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Proveedores y Descripción */}
              {currentStep === 5 && (
                <div data-testid="wizard-step-5">
                  <div className="modulo-form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Paso 5a: Datos de Proveedores</h3>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowProveedorModal(true)}>
                        <Plus size={16} /> Agregar Proveedor
                      </button>
                    </div>
                    {formData.campos_especificos.proveedores?.length > 0 ? (
                      <div className="table-wrapper">
                        <table className="detail-table">
                          <thead>
                            <tr><th>Nombre</th><th>NIT</th><th>Oferta (Q)</th><th></th></tr>
                          </thead>
                          <tbody>
                            {formData.campos_especificos.proveedores.map((prov, index) => (
                              <tr key={index}>
                                <td><strong>{prov.nombre}</strong></td>
                                <td>{prov.nit}</td>
                                <td>{prov.oferta ? `Q ${parseFloat(prov.oferta).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                                <td><button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveProveedor(index)}><Trash2 size={14} /></button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                        No hay proveedores agregados. Use el botón "Agregar Proveedor" para incluir datos.
                      </p>
                    )}
                  </div>

                  <div className="modulo-form-section">
                    <h3 className="modulo-form-section-title">Paso 5b: Descripción y Documentación</h3>
                    <div className="form-field">
                      <label className="required">Descripción / Hallazgo</label>
                      <RichTextEditor
                        value={formData.descripcion}
                        onChange={(value) => handleChange('descripcion', value)}
                        placeholder="Descripción detallada del hallazgo identificado..."
                        minHeight="150px"
                      />
                    </div>
                    <div className="form-field" style={{ marginTop: '1rem' }}>
                      <label>Links de OneDrive (Documentación de Respaldo)</label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="url"
                          value={currentLink}
                          onChange={(e) => setCurrentLink(e.target.value)}
                          placeholder="https://onedrive.com/..."
                        />
                        <button type="button" className="btn btn-secondary" onClick={handleAddLink}>
                          Agregar
                        </button>
                      </div>
                      {formData.links_referenciales.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          {formData.links_referenciales.map((link, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: 'var(--color-background)', borderRadius: '4px', marginBottom: '0.25rem' }}>
                              <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem' }}>{link}</a>
                              <button type="button" onClick={() => handleRemoveLink(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}>
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje de confirmación antes de crear */}
              {currentStep === 5 && showConfirmacion && (
                <div style={{
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                  border: '2px solid #FF9800',
                  borderRadius: '8px',
                  padding: '1rem 1.25rem',
                  marginTop: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }} data-testid="confirmacion-crear-gestion">
                  <AlertTriangle size={24} color="#E65100" />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#E65100' }}>
                      ¿Está seguro de crear la gestión?
                    </p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#BF360C' }}>
                      Revise que toda la información esté correcta antes de confirmar. Una vez creada, puede editar los datos desde el detalle de la gestión.
                    </p>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => setShowConfirmacion(false)}
                    data-testid="confirmacion-cancelar"
                  >
                    Volver a revisar
                  </button>
                </div>
              )}

              {/* Wizard Navigation */}
              <div className="wizard-navigation" data-testid="wizard-navigation">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => { 
                    setView('list'); 
                    setCurrentStep(1); 
                    setStepErrors({}); 
                    setFormData({
                      institucion: '',
                      nombre_asesor_cnc: '',
                      nombre_asesor_instancia: '',
                      fecha_asignacion: new Date().toISOString().split('T')[0],
                      fecha_vencimiento: '',
                      descripcion: '',
                      links_referenciales: [],
                      campos_especificos: {
                        color_bandera: '',
                        indicador_id: '',
                        indicador_codigo: '',
                        fecha_inicio_analisis: '',
                        fecha_fin_analisis: '',
                        unidad_compradora: '',
                        cantidad_nog: 0,
                        cantidad_npg: 0,
                        enlaces_guatecompras: [],
                        detalle_nogs: [],
                        detalle_npgs: [],
                        proveedores: []
                      }
                    });
                  }} 
                  disabled={loading}
                >
                  Cancelar
                </button>
                <div className="wizard-nav-center">
                  <span className="wizard-step-counter">Paso {currentStep} de 5</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {currentStep > 1 && (
                    <button type="button" className="btn btn-secondary" onClick={handlePrevStep} data-testid="wizard-btn-anterior">
                      <ChevronLeft size={16} /> Anterior
                    </button>
                  )}
                  {currentStep < 5 ? (
                    <button type="button" className="btn btn-primary" onClick={handleNextStep} data-testid="wizard-btn-siguiente">
                      Siguiente <ChevronRight size={16} />
                    </button>
                  ) : !showConfirmacion ? (
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={() => setShowConfirmacion(true)}
                      disabled={loading}
                      data-testid="wizard-btn-confirmar"
                    >
                      Crear Gestión <Check size={16} />
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary" disabled={loading} data-testid="wizard-btn-crear">
                      {loading ? 'Guardando...' : 'Confirmar y Crear'}
                      {!loading && <Check size={16} />}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {view === 'stats' && (
          <EstadisticasModulo 
            tipoGestion="Adquisiciones públicas" 
            colorModulo="#F44336"
          />
        )}
      </div>

      {/* Modal para agregar NOG */}
      {showNogModal && (
        <div className="modal-overlay" onClick={() => setShowNogModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Agregar Detalle de NOG</h3>
              <button className="btn-cerrar" onClick={() => setShowNogModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label className="required">NOG</label>
                  <input
                    type="text"
                    value={currentNog.nog}
                    onChange={(e) => setCurrentNog({...currentNog, nog: e.target.value})}
                    placeholder="Número de Operación Guatecompras"
                  />
                </div>
                <div className="form-field" style={{ gridColumn: 'span 2' }}>
                  <label>Descripción</label>
                  <input
                    type="text"
                    value={currentNog.descripcion}
                    onChange={(e) => setCurrentNog({...currentNog, descripcion: e.target.value})}
                    placeholder="Descripción del NOG"
                  />
                </div>
                <div className="form-field">
                  <label>Tipo Concurso</label>
                  <select
                    value={currentNog.tipo_concurso}
                    onChange={(e) => setCurrentNog({...currentNog, tipo_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {tiposConcurso.map(tipo => (
                      <option key={tipo.id} value={tipo.value}>{tipo.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Estado Concurso</label>
                  <select
                    value={currentNog.status_concurso}
                    onChange={(e) => setCurrentNog({...currentNog, status_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {estadosConcurso.map(estado => (
                      <option key={estado.id} value={estado.value}>{estado.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Fecha Adjudicación</label>
                  <input
                    type="date"
                    value={currentNog.fecha_adjudicacion}
                    onChange={(e) => setCurrentNog({...currentNog, fecha_adjudicacion: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Monto (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentNog.monto}
                    onChange={(e) => setCurrentNog({...currentNog, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNogModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddNog}>
                Agregar NOG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar NPG */}
      {showNpgModal && (
        <div className="modal-overlay" onClick={() => setShowNpgModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Agregar Detalle de NPG (Concurso)</h3>
              <button className="btn-cerrar" onClick={() => setShowNpgModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label className="required">NPG</label>
                  <input
                    type="text"
                    value={currentNpg.npg}
                    onChange={(e) => setCurrentNpg({...currentNpg, npg: e.target.value})}
                    placeholder="Número de Proceso Guatecompras"
                  />
                </div>
                <div className="form-field" style={{ gridColumn: 'span 2' }}>
                  <label>Descripción</label>
                  <input
                    type="text"
                    value={currentNpg.descripcion}
                    onChange={(e) => setCurrentNpg({...currentNpg, descripcion: e.target.value})}
                    placeholder="Descripción del NPG"
                  />
                </div>
                <div className="form-field">
                  <label>Tipo Concurso</label>
                  <select
                    value={currentNpg.tipo_concurso}
                    onChange={(e) => setCurrentNpg({...currentNpg, tipo_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {tiposConcurso.map(tipo => (
                      <option key={tipo.id} value={tipo.value}>{tipo.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Estado Concurso</label>
                  <select
                    value={currentNpg.status_concurso}
                    onChange={(e) => setCurrentNpg({...currentNpg, status_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {estadosConcurso.map(estado => (
                      <option key={estado.id} value={estado.value}>{estado.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Fecha Adjudicación</label>
                  <input
                    type="date"
                    value={currentNpg.fecha_adjudicacion}
                    onChange={(e) => setCurrentNpg({...currentNpg, fecha_adjudicacion: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Monto (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentNpg.monto}
                    onChange={(e) => setCurrentNpg({...currentNpg, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNpgModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddNpg}>
                Agregar NPG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar Proveedor */}
      {showProveedorModal && (
        <div className="modal-overlay" onClick={() => setShowProveedorModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Agregar Proveedor</h3>
              <button className="btn-cerrar" onClick={() => setShowProveedorModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field" style={{ gridColumn: 'span 2' }}>
                  <label className="required">Nombre del Proveedor</label>
                  <input
                    type="text"
                    value={currentProveedor.nombre}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, nombre: e.target.value})}
                    placeholder="Nombre completo del proveedor"
                  />
                </div>
                <div className="form-field">
                  <label className="required">NIT</label>
                  <input
                    type="text"
                    value={currentProveedor.nit}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, nit: e.target.value})}
                    placeholder="Número de NIT"
                  />
                </div>
                <div className="form-field">
                  <label>Oferta (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentProveedor.oferta}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, oferta: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProveedorModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddProveedor}>
                Agregar Proveedor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuloBanderasRojas;
